import {Injectable} from '@angular/core';
import {environment} from "../../environments/environment";
import {HttpClient, HttpParams} from "@angular/common/http";
import {Observable} from "rxjs";
import {PaymentTransactionsResponse} from "../models/paymentTransactions .model";
import {PaymentTransactionFilterModel} from "../interface/paymentTransactionFilterModel ";
import {TransactionAndTransferInfoModel} from "../models/transactionAndTransferInfo.model";
import {Page} from "../interface/hashtag";

@Injectable({
  providedIn: 'root'
})
export class TransactionService {

  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {
  }

  getTransactionsByTeamID(teamId: number, trimmedQuery?: string): Observable<PaymentTransactionsResponse> {
    const params: Record<string, string> = {
      teamId: teamId.toString(),
    };
    if (trimmedQuery) {
      params['search'] = trimmedQuery;
    }
    return this.http.get<PaymentTransactionsResponse>(`${this.apiUrl}/payment-transactions/history/team`, {params});
  }

  getAllIndividualPaymentTransactions(fromUserId: number | BigInt, page: number, size: number, trimmedQuery?: string): Observable<PaymentTransactionsResponse> {
    let params = this.createHttpParams(fromUserId, page, size, null, trimmedQuery)
    return this.http.get<PaymentTransactionsResponse>(`${this.apiUrl}/transaction-management`, {params});
  }

  filterIndividualPaymentTransactions(result: PaymentTransactionFilterModel, page: number, size: number, toUserId: bigint): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(toUserId, page, size, null)
    return this.http.post<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/individual/filter`, result, {params})
  }

  changeTransactionsIsSeen(toUserId: bigint): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/payment-transactions/change-transactions-status?toUserId=${toUserId}`, null);
  }

  changeTransactionsIsSeenForMyTeam(teamId: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/payment-transactions/change-transactions-status-for-team?teamId=${teamId}`, null);
  }

  changeTransactionsIsSeenForSelectedTeam(teamId: number, currentUserId: bigint): Observable<void> {
    const params = new HttpParams()
      .set('teamId', teamId.toString())
      .set('userId', currentUserId.toString());
    return this.http.put<void>(`${this.apiUrl}/payment-transactions/change-transactions-status-for-selected-team`, null, {params});
  }

  getRequestsTransfers(fromUserId: BigInt, page: number, size: number, trimmedQuery?: string): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(fromUserId, page, size, null, trimmedQuery)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/transfers`, {params});
  }

  getRequestsTransfersForMyTeam(teamId: number, page: number, size: number, trimmedQuery?: string): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(null, page, size, teamId, trimmedQuery)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/my-team/transfers`, {params});
  }

  getRequestsTransfersForSelectedTeam(teamId: number, userId: bigint, page: number, size: number, trimmedQuery?: string): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(userId, page, size, teamId, trimmedQuery)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/selected-team/transfers`, {params});
  }

  getIncomingTransactions(fromUserId: BigInt, page: number, size: number, trimmedQuery?: string): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(fromUserId, page, size, null, trimmedQuery)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/incoming`, {params});
  }

  getIncomingTransactionsForMyTeam(teamId: number, page: number, size: number, trimmedQuery?: string): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(null, page, size, teamId, trimmedQuery)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/my-team/incoming`, {params});
  }

  getIncomingTransactionsForSelectedTeam(teamId: number, currentUserId: bigint, page: number, size: number, trimmedQuery?: string): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(currentUserId, page, size, teamId, trimmedQuery)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/selected-team/incoming`, {params});
  }

  private createHttpParams(userId: number | BigInt | null | undefined, page: number, size: number, teamId: number | null | undefined, searchQuery?: string | null): HttpParams {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (userId !== null && userId !== undefined) {
      params = params.set('userId', userId.toString());
    }

    if (teamId !== null && teamId !== undefined) {
      params = params.set('teamId', teamId.toString());
    }

    if (searchQuery) {
      params = params.set('search', searchQuery);
    }
    return params;
  }

  getMyTeamAllPaymentTransactions(teamId: number, currentPage: number, pageSize: number, trimmedQuery?: string): Observable<PaymentTransactionsResponse> {
    let params = this.createHttpParams(null, currentPage, pageSize, teamId, trimmedQuery)
    return this.http.get<PaymentTransactionsResponse>(`${this.apiUrl}/transaction-management/my-team`, {params});
  }

  getSelectedTeamAllPaymentTransactions(teamId: number, currentUserId: bigint, currentPage: number, pageSize: number, trimmedQuery?: string): Observable<PaymentTransactionsResponse> {
    let params = this.createHttpParams(currentUserId, currentPage, pageSize, teamId, trimmedQuery)
    return this.http.get<PaymentTransactionsResponse>(`${this.apiUrl}/transaction-management/selected-team`, {params});
  }

  getAllIndividualPaymentTransactionsForSuperAdmin(page: number, size: number, trimmedQuery?: string): Observable<PaymentTransactionsResponse> {
    let params = this.createHttpParams(null, page, size, null, trimmedQuery)
    return this.http.get<PaymentTransactionsResponse>(`${this.apiUrl}/transaction-management/admin/all-individuals`, {params});
  }

  getIncomingTransactionsForSuperAdmin(page: number, size: number, trimmedQuery?: string): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(null, page, size, null, trimmedQuery)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/admin/incoming`, {params});
  }

  getIncomingTransactionsForSelectedTeamByTeamId(teamId:  number | undefined, page: number, size: number, trimmedQuery?: string): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(null, page, size, teamId, trimmedQuery)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/admin/incoming/by-teamId`, {params});
  }

  getRequestsTransfersForSuperAdmin(page: number, size: number): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(null, page, size, null, null)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/admin/transfers`, {params});
  }

  getTeamsRequestsTransfersForSuperAdmin(selectedTeamId: number | undefined, page: number, size: number): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(null, page, size, selectedTeamId)
    return this.http.get<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/admin/teams-transfers`, {params});
  }

  changeTransactionsIsAdminSeen(toUserId: bigint | undefined): Observable<void> {
    const params = toUserId ? `?toUserId=${toUserId}` : '';
    const url = `${this.apiUrl}/payment-transactions/admin/change-status${params}`;
    return this.http.put<void>(url, null);
  }

  changeTeamsTransactionsIsAdminSeen(teamId: bigint | undefined): Observable<void> {
    const params = teamId ? `?teamId=${teamId}` : '';
    const url = `${this.apiUrl}/payment-transactions/admin/teams/change-status${params}`;
    return this.http.put<void>(url, null);
  }

  changeRequestsIsAdminSeen(toUserId: bigint | undefined): Observable<void> {
    const params = toUserId ? `?toUserId=${toUserId}` : '';
    const url = `${this.apiUrl}/internalTransfer/admin/transfers/mark-seen${params}`;
    return this.http.put<void>(url, null);
  }

  changeTeamsRequestsIsAdminSeen(teamId: number | undefined): Observable<void> {
    const params = teamId ? `?teamId=${teamId}` : '';
    const url = `${this.apiUrl}/internalTransfer/admin/teams-transfers/mark-seen${params}`;
    return this.http.put<void>(url, null);
  }

  getAllTeamsPaymentTransactions(teamId: number | undefined, currentPage: number, pageSize: number, trimmedQuery?: string): Observable<PaymentTransactionsResponse> {
    let params = this.createHttpParams(null, currentPage, pageSize, teamId, trimmedQuery)
    return this.http.get<PaymentTransactionsResponse>(`${this.apiUrl}/transaction-management/admin/teams`, {params});
  }

  changeRequestStatus(id: number, status: string): Observable<void> {
    const params = new HttpParams()
      .set('id', id.toString())
      .set('status', status);
    return this.http.put<void>(`${this.apiUrl}/internalTransfer/change-status`, null, {params});
  }

  filterAllUsersIndividualPaymentTransactions(result: PaymentTransactionFilterModel, page: number, size: number, toUserId: bigint | undefined | null): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(toUserId, page, size, null)
    return this.http.post<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/admin/individual/filter`, result, {params})
  }

  filterAllTeamsIndividualPaymentTransactions(result: PaymentTransactionFilterModel, page: number, size: number, teamId: number | undefined | null): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(null, page, size, teamId)
    return this.http.post<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/admin/teams/filter`, result, {params})
  }

  filterMyTeamIndividualPaymentTransactions(currentUserId: bigint, result: PaymentTransactionFilterModel, page: number, size: number, teamId: number): Observable<Page<TransactionAndTransferInfoModel>> {
    let params = this.createHttpParams(currentUserId, page, size, teamId)
    return this.http.post<Page<TransactionAndTransferInfoModel>>(`${this.apiUrl}/transaction-management/team/filter`, result, {params})
  }
}
